import * as React from "react";
import {useEffect, useState} from "react";
import {
    Badge,
    classNames,
    Dialog,
    DialogAlignment,
    KeyPad,
    layout,
    MessageOverlay,
    PhoneDigits,
    PushButton,
    TextInput,
    useAsyncCallback,
    usePlatform,
    useStorageItemValue,
    useTypeRef
} from "@variocube/cube-ui";
import {DefaultSignUpUrl, SignUpUrlKey} from "../model";
import {dialogStyles} from "./dialogStyles";
import {useLocalization} from "../../i18n";

interface SignupDialogProps {
    open: boolean;
    onClose: () => void;
}

export function SignupDialog({open, onClose}: SignupDialogProps) {
    const signupUrl = useStorageItemValue<string>(SignUpUrlKey) ?? DefaultSignUpUrl;
    const platform = usePlatform();
    const {t, language} = useLocalization();
    const [type, setType] = useTypeRef();
    const [phone, setPhone] = useState("");

    const signup = useAsyncCallback(async () => {
        if (phone) {
            const response = await window.fetch(signupUrl, {
                method: "POST",
                body: JSON.stringify({
                    phone,
                    hostname: platform.hostname,
                    locale: language,
                }),
                headers: {"Content-type": "application/json"}
            });
            return response.ok;
        }
    }, [signupUrl, phone, language]);

    useEffect(() => {
        if (open) {
            setPhone("");
            signup.clear();
        }
    }, [open, signup.clear]);

    return (
        <Dialog open={open} onBackdropClick={onClose} align={DialogAlignment.Bottom}>

            <div className={layout.horizontal}>
                <div className={classNames(layout.vertical, layout.spaceBetween, dialogStyles.info)}>
                    <div>
                        <div className={dialogStyles.heading}>
                            {t("EnterPhoneNumber")}
                        </div>
                        <div className={dialogStyles.description}>
                            {t("EnterPhoneNumberDescription")}
                        </div>
                    </div>
                    <div>
                        {signup.error && (
                            <Badge error>
                                {t("SignUpFailed")}
                            </Badge>
                        )}
                        {signup.loading && (
                            <Badge>
                                {t("SignUpPending")}
                            </Badge>
                        )}
                    </div>
                    <div>
                        <PushButton primary onClick={signup.execute} disabled={signup.loading} className={dialogStyles.button}>
                            {t("Submit")}
                        </PushButton>
                        <PushButton secondary onClick={onClose} disabled={signup.loading} className={dialogStyles.button}>
                            {t("Cancel")}
                        </PushButton>
                    </div>
                </div>
                <div className={dialogStyles.action}>
                    <TextInput
                        type={"phone"}
                        typeRef={setType}
                        onChange={setPhone}
                        disabled={signup.loading}
                        value={phone}/>
                    <KeyPad onType={type} disabled={signup.loading} digits={PhoneDigits}/>
                </div>
                <MessageOverlay
                    success
                    open={Boolean(signup.result)}
                    message={t("SignUpSuccess")}
                    strings={t}
                    onClose={onClose}
                    autoClose={10}
                />
            </div>
        </Dialog>
    );
}
